import getProjectIdFromLocation from "../../services/getters/location_metadata/get_project_id_from_location";
import makeEventfulAction from "../make_eventful_action";
import getFloorsByFirebaseId from "../../services/getters/floor_getters/get_floors_by_firebase_id";
import runPipeline from "../run_pipeline";
import getScanDatasetId from "../../services/getters/scan_dataset_getters/get_scan_dataset_id";
import getMostRecentScanDatasetForFloor from "../../services/getters/scan_dataset_getters/get_most_recent_scan_dataset_for_floor";
import loadScanDatasetsForFloor from "../react_query/scan_datasets/load_scan_datasets_for_floor";

import type { IfcExportFileNamingLabels } from "../../services/utilities/ifc_export/ifc_export_file_names";

/**
 * `useMostRecentScan` resolves each floor to its own most recent pointcloud
 * capture, not whichever is open. As-built names that scan, so it must use it.
 * `fileNaming` carries the filename tokens the pipeline can't derive.
 */
const exportBatchIfc = (floorIds: any[], ifcType?: string, minimumDeviationMetres?: number, tradeCodes?: string[], useMostRecentScan?: boolean, fileNaming?: IfcExportFileNamingLabels) => {
  return async (dispatch, getState) => {
    const firebaseProjectId = getProjectIdFromLocation(getState(), {});
    const selectedScanDatasetId = getScanDatasetId(getState(), {});
    const resolveMostRecentScan = ifcType === "current_deviations" || !!useMostRecentScan;

    // The user may never have opened the other floors, so load their scans
    // first. A per-floor failure falls back below rather than aborting.
    if (resolveMostRecentScan) {
      await Promise.all(floorIds.map(floorId =>
        loadScanDatasetsForFloor({ projectId: firebaseProjectId, floorId })(dispatch, getState).catch(() => null)
      ));
    }

    // Re-read state so the resolver sees the freshly-fetched scan datasets.
    const state = getState();
    let floors = getFloorsByFirebaseId(state, {});
    let obj: any = [];
    Object.values(floors).forEach(floor => {
      if (floorIds.includes(floor.firebaseId)) {
        const selectedOrDefaultScanDatasetId = floor.firebaseScanDatasetIds != null && floor.firebaseScanDatasetIds.includes(selectedScanDatasetId) ? selectedScanDatasetId : floor.defaultFirebaseScanDatasetId;
        // Element states come from pointcloud data, so prefer the most recent
        // pointcloud capture; fall back so the batch never drops a floor.
        const firebaseScanDatasetId = resolveMostRecentScan
          ? (getMostRecentScanDatasetForFloor(state, { floorId: floor.firebaseId })?.firebaseId ?? selectedOrDefaultScanDatasetId)
          : selectedOrDefaultScanDatasetId;
        let instance: {firebaseFloorId: string, firebaseScanDatasetId: string} = {firebaseFloorId: floor.firebaseId, firebaseScanDatasetId};
        obj.push(instance);
      }
    });

    const options: {
      type: string,
      pipelineBatchFloors: string,
      minimum_deviation?: number,
      trades?: string,
      tolerance_label?: string,
      trades_label?: string
    } = {
      type: ifcType,
      pipelineBatchFloors: JSON.stringify(obj),
    };
    // Omit at the default so the pipeline keeps its historical 3".
    if (Number.isFinite(minimumDeviationMetres)) {
      options.minimum_deviation = minimumDeviationMetres;
    }
    // Only sent when trades are selected, so other export flows are unaffected.
    if (tradeCodes && tradeCodes.length > 0) {
      options.trades = JSON.stringify(tradeCodes);
    }
    // Filename tokens the pipeline can't derive on its own, so the exported files carry
    // the same names the export wizard previewed. Omitted when empty, which leaves the pipeline on
    // its own defaults for the export flows that don't collect them.
    if (fileNaming?.toleranceLabel) {
      options.tolerance_label = fileNaming.toleranceLabel;
    }
    if (fileNaming?.tradesLabel) {
      options.trades_label = fileNaming.tradesLabel;
    }

    return runPipeline({
      name: "generate-batch-ifc",
      firebaseProjectId,
      options,
    })(dispatch, getState);
  };
};

export default makeEventfulAction("exportBatchIfc", exportBatchIfc);
